import * as THREE from 'three';
import { MOB_CONFIG } from './config.js';

const mobHeadGeo = new THREE.BoxGeometry(0.62, 0.62, 0.62);
const mobBodyGeo = new THREE.BoxGeometry(0.62, 0.92, 0.36);
const mobLegGeo = new THREE.BoxGeometry(0.24, 0.78, 0.24);
const mobArmGeo = new THREE.BoxGeometry(0.2, 0.78, 0.2);
const villagerNoseGeo = new THREE.BoxGeometry(0.14, 0.2, 0.14);

const quadBodyGeo = new THREE.BoxGeometry(1.05, 0.64, 0.48);
const quadHeadGeo = new THREE.BoxGeometry(0.46, 0.44, 0.42);
const quadLegGeo = new THREE.BoxGeometry(0.2, 0.52, 0.2);

const chickenBodyGeo = new THREE.BoxGeometry(0.62, 0.54, 0.5);
const chickenHeadGeo = new THREE.BoxGeometry(0.34, 0.34, 0.32);
const chickenLegGeo = new THREE.BoxGeometry(0.08, 0.32, 0.08);
const chickenBeakGeo = new THREE.BoxGeometry(0.14, 0.08, 0.12);

const mobHitboxGeo = new THREE.BoxGeometry(0.9, 1.95, 0.9);
const hpBgGeo = new THREE.BoxGeometry(0.94, 0.11, 0.04);
const hpFillGeo = new THREE.BoxGeometry(0.88, 0.07, 0.05);

function addSharedDecorations(group, opts = {}) {
  const hpWidth = Math.max(0.5, Number(opts.hpWidth || 0.94));
  const hpY = Number.isFinite(opts.hpY) ? opts.hpY : 2.56;
  const hitboxSize = opts.hitboxSize || { x: 0.9, y: 1.95, z: 0.9 };
  const hitboxY = Number.isFinite(opts.hitboxY) ? opts.hitboxY : (hitboxSize.y * 0.5 - 0.015);

  const hpRoot = new THREE.Group();
  hpRoot.position.y = hpY;

  const hpBg = new THREE.Mesh(
    new THREE.BoxGeometry(hpWidth, 0.11, 0.04),
    new THREE.MeshBasicMaterial({ color: 0x1a1a1a, transparent: true, opacity: 0.86 })
  );
  const hpFill = new THREE.Mesh(
    new THREE.BoxGeometry(Math.max(0.1, hpWidth - 0.06), 0.07, 0.05),
    new THREE.MeshBasicMaterial({ color: 0x66ff66, transparent: true, opacity: 0.94 })
  );
  hpFill.position.set(0, 0, 0.011);
  hpRoot.add(hpBg);
  hpRoot.add(hpFill);
  group.add(hpRoot);

  const hitbox = new THREE.Mesh(
    new THREE.BoxGeometry(hitboxSize.x, hitboxSize.y, hitboxSize.z),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false })
  );
  hitbox.position.y = hitboxY;
  hitbox.userData.isMobHitbox = true;
  group.add(hitbox);

  return { hpRoot, hpFill, hitbox };
}

function createHumanoidMesh(type, cfg) {
  const group = new THREE.Group();
  const isVillager = type === 'villager';
  const skinMat = new THREE.MeshStandardMaterial({
    color: isVillager ? (cfg.accentColor || 0xd1ab84) : cfg.color,
    roughness: 0.82,
    metalness: 0.02,
    transparent: true
  });
  const darkMat = new THREE.MeshStandardMaterial({
    color: isVillager ? (cfg.color || 0x866140) : 0x222222,
    roughness: 0.9,
    metalness: 0.01,
    transparent: true
  });

  const body = new THREE.Mesh(mobBodyGeo, isVillager ? darkMat : skinMat);
  body.position.y = 1.15;
  group.add(body);

  const head = new THREE.Mesh(mobHeadGeo, skinMat);
  head.position.y = 1.95;
  group.add(head);

  const leftLeg = new THREE.Mesh(mobLegGeo, darkMat);
  leftLeg.position.set(-0.16, 0.39, 0);
  group.add(leftLeg);

  const rightLeg = new THREE.Mesh(mobLegGeo, darkMat);
  rightLeg.position.set(0.16, 0.39, 0);
  group.add(rightLeg);

  const leftArm = new THREE.Mesh(mobArmGeo, isVillager ? darkMat : skinMat);
  leftArm.position.set(-0.43, 1.1, 0);
  group.add(leftArm);

  const rightArm = new THREE.Mesh(mobArmGeo, isVillager ? darkMat : skinMat);
  rightArm.position.set(0.43, 1.1, 0);
  group.add(rightArm);

  if (type === 'creeper') {
    body.scale.set(1.08, 1.04, 1.08);
    head.scale.set(1.05, 1.05, 1.05);
    leftArm.visible = false;
    rightArm.visible = false;
  } else if (type === 'enderman') {
    body.scale.set(0.95, 1.24, 0.95);
    body.position.y = 1.28;
    head.scale.set(0.9, 0.9, 0.9);
    head.position.y = 2.22;

    leftLeg.scale.y = 1.58;
    rightLeg.scale.y = 1.58;
    leftLeg.position.y = 0.56;
    rightLeg.position.y = 0.56;

    leftArm.scale.y = 1.64;
    rightArm.scale.y = 1.64;
    leftArm.position.set(-0.45, 1.34, 0);
    rightArm.position.set(0.45, 1.34, 0);

    skinMat.emissive.setHex(0x2a1452);
    skinMat.emissiveIntensity = 0.05;
  } else if (type === 'villager') {
    body.scale.set(1.02, 1.08, 1.0);
    body.position.y = 1.2;
    head.scale.set(0.98, 0.98, 0.98);
    head.position.y = 1.96;

    leftArm.position.set(-0.14, 1.03, 0.16);
    rightArm.position.set(0.14, 1.03, 0.16);
    leftArm.rotation.set(-0.12, 0, 0.62);
    rightArm.rotation.set(-0.12, 0, -0.62);
    leftArm.scale.set(0.92, 0.92, 0.92);
    rightArm.scale.set(0.92, 0.92, 0.92);

    leftLeg.scale.y = 0.94;
    rightLeg.scale.y = 0.94;
    leftLeg.position.y = 0.37;
    rightLeg.position.y = 0.37;

    const nose = new THREE.Mesh(villagerNoseGeo, skinMat);
    nose.position.set(0, 1.87, 0.35);
    group.add(nose);
  }

  const deco = addSharedDecorations(group, type === 'enderman'
    ? {
      hpY: 3.18,
      hitboxSize: { x: 0.95, y: 2.75, z: 0.95 },
      hitboxY: 1.36
    }
    : {});
  return {
    group,
    hitbox: deco.hitbox,
    hpRoot: deco.hpRoot,
    hpFill: deco.hpFill,
    mats: [skinMat, darkMat]
  };
}

function createDragonMesh(cfg) {
  const group = new THREE.Group();
  const baseMat = new THREE.MeshStandardMaterial({ color: cfg.color, roughness: 0.74, metalness: 0.06, transparent: true });
  const accentMat = new THREE.MeshStandardMaterial({ color: cfg.accentColor || 0x6c4fd2, roughness: 0.68, metalness: 0.08, transparent: true });

  const body = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.82, 3.8), baseMat);
  body.position.set(0, 2.0, 0);
  group.add(body);

  const neck = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.42, 0.82), baseMat);
  neck.position.set(0, 2.1, 2.25);
  group.add(neck);

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.56, 1.25), accentMat);
  head.position.set(0, 2.08, 3.22);
  group.add(head);

  const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.18, 0.9), accentMat);
  jaw.position.set(0, 1.78, 3.34);
  group.add(jaw);

  const wingL = new THREE.Mesh(new THREE.BoxGeometry(4.8, 0.14, 2.2), accentMat);
  wingL.position.set(-3.02, 2.06, 0.15);
  group.add(wingL);

  const wingR = wingL.clone();
  wingR.position.x = 3.02;
  group.add(wingR);

  for (let i = 0; i < 4; i++) {
    const tail = new THREE.Mesh(new THREE.BoxGeometry(0.54 - i * 0.06, 0.34, 0.78), baseMat);
    tail.position.set(0, 2.0 - i * 0.04, -2.12 - i * 0.62);
    group.add(tail);
  }

  const deco = addSharedDecorations(group, {
    hpWidth: 2.8,
    hpY: 5.6,
    hitboxSize: { x: 5.8, y: 2.9, z: 5.8 },
    hitboxY: 2.0
  });
  return {
    group,
    hitbox: deco.hitbox,
    hpRoot: deco.hpRoot,
    hpFill: deco.hpFill,
    mats: [baseMat, accentMat],
    parts: { wingL, wingR, head }
  };
}

function createQuadrupedMesh(cfg) {
  const group = new THREE.Group();

  const baseMat = new THREE.MeshStandardMaterial({ color: cfg.color, roughness: 0.84, metalness: 0.01, transparent: true });
  const accentMat = new THREE.MeshStandardMaterial({ color: cfg.accentColor || cfg.color, roughness: 0.88, metalness: 0.0, transparent: true });
  const legMat = new THREE.MeshStandardMaterial({ color: 0x3a322b, roughness: 0.9, metalness: 0.0, transparent: true });

  const body = new THREE.Mesh(quadBodyGeo, baseMat);
  body.position.set(0, 1.08, 0);
  group.add(body);

  const head = new THREE.Mesh(quadHeadGeo, accentMat);
  head.position.set(0.7, 1.12, 0);
  group.add(head);

  const legPos = [
    [-0.34, 0.5, -0.15],
    [-0.34, 0.5, 0.15],
    [0.3, 0.5, -0.15],
    [0.3, 0.5, 0.15]
  ];

  for (const [x, y, z] of legPos) {
    const leg = new THREE.Mesh(quadLegGeo, legMat);
    leg.position.set(x, y, z);
    group.add(leg);
  }

  const deco = addSharedDecorations(group);
  return {
    group,
    hitbox: deco.hitbox,
    hpRoot: deco.hpRoot,
    hpFill: deco.hpFill,
    mats: [baseMat, accentMat, legMat]
  };
}

function createChickenMesh(cfg) {
  const group = new THREE.Group();

  const bodyMat = new THREE.MeshStandardMaterial({ color: cfg.color, roughness: 0.83, metalness: 0.0, transparent: true });
  const accentMat = new THREE.MeshStandardMaterial({ color: cfg.accentColor || 0xd7a167, roughness: 0.7, metalness: 0.0, transparent: true });
  const darkMat = new THREE.MeshStandardMaterial({ color: 0x644a2d, roughness: 0.88, metalness: 0.0, transparent: true });

  const body = new THREE.Mesh(chickenBodyGeo, bodyMat);
  body.position.set(0, 0.92, 0);
  group.add(body);

  const head = new THREE.Mesh(chickenHeadGeo, bodyMat);
  head.position.set(0.34, 1.18, 0);
  group.add(head);

  const beak = new THREE.Mesh(chickenBeakGeo, accentMat);
  beak.position.set(0.52, 1.12, 0);
  group.add(beak);

  const legL = new THREE.Mesh(chickenLegGeo, darkMat);
  legL.position.set(-0.1, 0.52, -0.11);
  group.add(legL);

  const legR = new THREE.Mesh(chickenLegGeo, darkMat);
  legR.position.set(-0.1, 0.52, 0.11);
  group.add(legR);

  const deco = addSharedDecorations(group);
  return {
    group,
    hitbox: deco.hitbox,
    hpRoot: deco.hpRoot,
    hpFill: deco.hpFill,
    mats: [bodyMat, accentMat, darkMat]
  };
}

export function createMobMesh(type) {
  const cfg = MOB_CONFIG[type];
  if (!cfg) return null;

  const built = cfg.model === 'quadruped'
    ? createQuadrupedMesh(cfg)
    : cfg.model === 'chicken'
      ? createChickenMesh(cfg)
      : cfg.model === 'dragon'
        ? createDragonMesh(cfg)
        : createHumanoidMesh(type, cfg);

  built.group.traverse((node) => {
    if (!node.isMesh) return;
    node.castShadow = false;
    node.receiveShadow = true;
  });

  return built;
}
